import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  setValidationErrors,
  showErrorToast,
} from '../../../../../../common/global/helpers';
import Breadcrumbs from '../../../../../../common/widgets/Breadcrumbs';
import { CardType } from '../../../../enums';
import { ComputedCardProductPriceModel } from '../../../../models/Cards';
import { Dictionary } from '../../../../models/Categories';
import { ErrorState, ErrorType } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { cardService } from '../../../../services/card';
import applicationCrumbs from '../../../ApplicationCrumbs';
import PlatformInformation from '../../../Common/PlatformInformation';
import MyAccountMenu from '../../Common/MyAccountMenu';
import { GenericCardData, GiftCardData } from '../Common/types';
import { CardFormFactorSelector } from './CardFormFactorSelector';
import { CardTypeSelector } from './CardTypeSelector';
import { Summary } from './Summary';
import { TopUpCard } from './TopUpCard';
import { BuyCardPageState } from './common';

export const BuyCardPage = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const crumbs = applicationCrumbs(translate);
  const [state, setState] = useState<BuyCardPageState>({
    currentStep: 1,
    step1: {},
    step2: {},
    step3: {},
    step4: {},
  });
  const [errors, setErrors] = useState<ErrorState>({});
  const onBackButtonClick = () => {
    setErrors({});

    if (state.currentStep === 1) {
      navigate(-1);
    } else {
      setState(
        produce(d => {
          d.currentStep -= 1;
        }),
      );
    }
  };
  const selectCardType = (
    cardType: CardType,
    thumbnail?: string,
    mainImage?: string,
  ) => {
    setState(
      produce(d => {
        if (!!d.selectedCard && d.selectedCard.type !== cardType) {
          d.step3 = {};
        }
        d.selectedCard = {
          type: cardType,
          thumbnail,
          mainImage,
        };
        d.currentStep += 1;
      }),
    );
  };

  const saveTopUpData = async (
    productId: number,
    options: Dictionary<any>,
    serializedOptions: string,
    price: ComputedCardProductPriceModel,
  ) => {
    setState(
      produce(d => {
        if (d.step3.genericCard) {
          d.step3.genericCard.isSaveButtonLoading = true;
        }
        if (d.step3.giftCard) {
          d.step3.giftCard.isSaveButtonLoading = true;
        }
      }),
    );
    const resp = await cardService.validateOptions({
      productId,
      options: serializedOptions,
    });
    if (resp.hasErrors) {
      setState(
        produce(d => {
          if (d.step3.genericCard) {
            d.step3.genericCard.isSaveButtonLoading = false;
          }
          if (d.step3.giftCard) {
            d.step3.giftCard.isSaveButtonLoading = false;
          }
        }),
      );
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      setState(
        produce(d => {
          if (d.step3.genericCard) {
            d.step3.genericCard.isSaveButtonLoading = false;
          }
          if (d.step3.giftCard) {
            d.step3.giftCard.isSaveButtonLoading = false;
          }
          d.selectedCard!.productId = productId;
          d.selectedCard!.options = options;
          d.selectedCard!.serializedOptions = serializedOptions;
          d.selectedCard!.price = price;
          d.currentStep += 1;
        }),
      );
    }
  };

  const setStep3GiftData = (data: GiftCardData) => {
    setState(
      produce(d => {
        d.step3 = { giftCard: data };
      }),
    );
  };
  const setStep3GenericData = (data: GenericCardData) => {
    setState(
      produce(d => {
        d.step3 = { genericCard: data };
      }),
    );
  };

  const clearErrors = (optionId: number | string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[optionId] = [];
          draft.null = [];
        }
      }),
    );
  };

  return (
    <>
      <Breadcrumbs entries={[crumbs.home, crumbs.cards]} />
      <section className="section-main section-actions my-account-page card-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <ProgressBar currentStep={state.currentStep} />
            <div className="panel padding-xl">
              <div className="card-steps">
                <a onClick={onBackButtonClick} className="btn btn-panel-back">
                  <span className="btn-text">{translate(resources.back)}</span>
                </a>
                <div className="step-no">
                  {translate(resources.employeeCards.buy.step, {
                    0: `${state.currentStep}/4`,
                  })}
                </div>
              </div>
              {state.currentStep === 1 && (
                <CardTypeSelector
                  selectCardType={selectCardType}
                  cardTypes={state.step1.cardTypes}
                  setCardTypes={cardTypes =>
                    setState(
                      produce(d => {
                        d.step1.cardTypes = cardTypes;
                      }),
                    )
                  }
                />
              )}
              {state.currentStep === 2 && (
                <CardFormFactorSelector
                  cardType={state.selectedCard!.type}
                  selectedCardFormFactor={state.step2.cardFormFactor}
                  onCardFormFactorChange={cardFormFactor =>
                    setState(
                      produce(d => {
                        d.step2.cardFormFactor = cardFormFactor;
                      }),
                    )
                  }
                  onContinueClick={() =>
                    setState(
                      produce(d => {
                        d.currentStep += 1;
                      }),
                    )
                  }
                />
              )}
              {state.currentStep === 3 && (
                <TopUpCard
                  selectedCard={state.selectedCard}
                  step3={state.step3}
                  setStep3GiftData={setStep3GiftData}
                  setStep3GenericData={setStep3GenericData}
                  saveTopUpData={saveTopUpData}
                  errors={errors}
                  setErrors={setErrors}
                  clearErrors={clearErrors}
                  cardFormFactor={state.step2.cardFormFactor}
                />
              )}
              {state.currentStep === 4 && (
                <Summary
                  cardFormFactor={state.step2.cardFormFactor!}
                  selectedCard={state.selectedCard!}
                  customerAddressModel={state.step4.customerAddressModel}
                  setCustomerAddressModel={e =>
                    setState(
                      produce(d => {
                        d.step4.customerAddressModel = e;
                      }),
                    )
                  }
                  errors={errors}
                  setErrors={setErrors}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const ProgressBar = (props: { currentStep: number }) => {
  const { currentStep } = props;
  let width;
  switch (currentStep) {
    case 1:
      width = '25%';
      break;
    case 2:
      width = '50%';
      break;
    case 3:
      width = '75%';
      break;
    case 4:
      width = '100%';
      break;
    default:
      width = '0%';
  }
  return (
    <div className="steps-progess-bar">
      <div className="progess-bar-step" style={{ width }} />
    </div>
  );
};
